/**
 * "Upcoming reminders" — the bell's section for reminders that have not fired yet.
 *
 * The bell is the ONLY global surface for reminders (there is no notification-centre
 * page — `docs/platform/entity-reminders-design.md`), and it is also the only place a
 * reminder SOMEBODY ELSE set for you is visible: `RemindMeButton`'s popover lists the
 * caller's own reminders on one record, this lists everything the caller will be pinged
 * about, wherever it lives.
 *
 * Two consequences shape the rows:
 *
 *  - **Cancel is creator-only**, matching the API. A recipient who is not the creator
 *    gets no cancel button rather than one that 403s — v1 has no "remove just me".
 *  - **The entity link is resolved through the vocabulary**, not guessed. A reminder's
 *    `entity_type` is the short follow noun (`deal`, `sales_order`), and the URL registry
 *    is keyed by the canonical `{module}.{entity}`, so the two are joined by
 *    `tryFromLegacy` — the one translation layer — and a row whose type resolves to
 *    nothing renders as plain text instead of a dead link.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { Button } from '../../ui/button';
import { BidiText, DisclosureChevron } from '../../../shared/components/common';
import { LocalizedDateTimeDisplay } from '../../../shared/components/LocalizedDateTimeDisplay';
import {
  useCancelEntityReminder,
  type EntityReminder
} from '../../../shared/notifications/useEntityReminders';
import { getEntityUrl } from '../../../shared/utils/entityUrls';
import {
  LegacySpace,
  tryFromLegacy
} from '../../../shared/utils/entityVocabulary';

interface UpcomingRemindersSectionProps {
  /**
   * Always non-empty: the bell renders this section only when there is something in it,
   * so an "up next" heading never sits over blank space, and a user with no reminders
   * sees the bell exactly as it was.
   */
  reminders: EntityReminder[];
  /** The viewer, so a row they did not create renders without a cancel affordance. */
  currentUserId: string | null;
  /** Navigate and close the bell — the bell owns both halves. */
  onNavigate: (href: string) => void;
}

/** Where a reminder's row links, or null when its entity is not URL-addressable. */
export function reminderHref(reminder: EntityReminder): string | null {
  const entityType = tryFromLegacy(
    reminder.entity_type,
    LegacySpace.FollowEntityType
  );
  if (!entityType) return null;
  return getEntityUrl({
    entityType,
    entityId: reminder.entity_id,
    // Reminders are persisted rows, so this runs over historical entity_types too. A
    // convention-guessed URL for one of those is worse than no link at all.
  });
}

export function UpcomingRemindersSection({
  reminders,
  currentUserId,
  onNavigate
}: UpcomingRemindersSectionProps) {
  const { t } = useTranslation('common');
  // Open by default: a collapsed section whose whole content is three short rows hides
  // the feature behind a click for no gain in a popover this size.
  const [expanded, setExpanded] = useState(true);
  const cancelReminder = useCancelEntityReminder();

  return (
    <div className="border-b bg-muted/30">
      <Button
        variant="ghost"
        className="h-auto w-full justify-between rounded-none px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
        aria-expanded={expanded}
        aria-controls="upcoming-reminders-list"
        // Spelled out, because the accessible name is the concatenation of the two spans
        // below with no separator between them — "Upcoming reminders2" is what a screen
        // reader would otherwise announce.
        aria-label={`${t('reminders.upcoming')} (${reminders.length})`}
        onClick={() => setExpanded((current) => !current)}
      >
        <span>
          {t('reminders.upcoming')}
          <span className="ms-1.5">{reminders.length}</span>
        </span>
        <DisclosureChevron open={expanded} />
      </Button>

      {expanded && (
        <ul id="upcoming-reminders-list" className="pb-1">
          {reminders.map((reminder) => {
            const href = reminderHref(reminder);
            const label =
              reminder.entity_title || t('reminders.untitledEntity');
            return (
              <li key={reminder.id} className="flex items-start gap-1 px-1">
                <Button
                  variant="ghost"
                  className="h-auto min-w-0 flex-1 flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-left font-normal"
                  disabled={!href}
                  onClick={() => href && onNavigate(href)}
                >
                  <BidiText className="w-full truncate text-sm text-foreground">
                    {label}
                  </BidiText>
                  {reminder.note && (
                    <BidiText className="w-full truncate text-xs text-muted-foreground">
                      {reminder.note}
                    </BidiText>
                  )}
                  <LocalizedDateTimeDisplay
                    date={reminder.remind_at}
                    className="text-[11px] text-muted-foreground"
                  />
                  {reminder.created_by !== currentUserId &&
                    reminder.created_by_name && (
                      <span className="text-[11px] text-muted-foreground">
                        {t('reminders.setBy', {
                          name: reminder.created_by_name
                        })}
                      </span>
                    )}
                </Button>
                {reminder.created_by === currentUserId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    aria-label={t('reminders.cancelAction')}
                    disabled={cancelReminder.isPending}
                    onClick={() =>
                      cancelReminder.mutate({ reminderId: reminder.id })
                    }
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
